import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildPreviewAdminProspectLetterTemplatePdfRequest,
  previewAdminProspectLetterTemplatePdf,
} from '../../../services/admin/prospectLetterTemplatesService.js';
import {
  assertCallableSafeLetterTemplatePayload,
  cloneCallableSafeLetterTemplateJson,
} from '../../../services/admin/letterTemplateCallableSafety.js';
import {
  adminProspectLetterTemplatePdfPreviewErrorKeyFromError,
  classifyAdminCallableError,
} from '../adminProspectLetterTemplateDisplay.js';
import {
  closeLetterTemplatePdfPreviewWindow,
  createTransientPdfPreviewFromBase64,
  navigateLetterTemplatePdfPreviewWindow,
  openLetterTemplatePdfPreviewWindow,
  revokePdfObjectUrl,
} from '../../../services/admin/letterTemplatePdfPreview.js';

/**
 * Transient synthetic PDF preview for unsaved letter-template drafts.
 * Revokes object URLs on replace/unmount; never persists preview bytes.
 */
export function useLetterTemplatePdfPreview({ enabled = true }) {
  const objectUrlRef = useRef(null);
  const inFlightRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [retryable, setRetryable] = useState(false);

  const revokeCurrentUrl = useCallback(() => {
    revokePdfObjectUrl(objectUrlRef.current);
    objectUrlRef.current = null;
  }, []);

  useEffect(() => () => revokeCurrentUrl(), [revokeCurrentUrl]);

  const previewDraft = useCallback(
    async ({ bilingual, frenchDocument, englishDocument, translation, translationStale }) => {
      if (!enabled || inFlightRef.current) return;

      inFlightRef.current = true;
      setPending(true);
      setErrorKey(null);
      setRetryable(false);
      revokeCurrentUrl();

      const previewWindow = openLetterTemplatePdfPreviewWindow();
      if (!previewWindow) {
        inFlightRef.current = false;
        setPending(false);
        setErrorKey('admin.prospection.letterTemplate.pdfPreview.popupBlocked');
        return;
      }

      try {
        const safeFr = cloneCallableSafeLetterTemplateJson(frenchDocument);
        const safeEn = bilingual ? cloneCallableSafeLetterTemplateJson(englishDocument) : null;
        const documents = { fr: safeFr };
        if (bilingual && safeEn) documents.en = safeEn;

        const safety = assertCallableSafeLetterTemplatePayload({ bilingual, documents });
        if (!safety.ok) {
          const err = new Error('letter_template_payload_not_callable_safe');
          err.code = 'letter_template_payload_not_callable_safe';
          throw err;
        }

        const request = buildPreviewAdminProspectLetterTemplatePdfRequest({
          bilingual,
          documents,
          translation: translation && !translationStale ? translation : undefined,
        });

        const result = await previewAdminProspectLetterTemplatePdf(request);
        const { objectUrl } = createTransientPdfPreviewFromBase64({
          base64: result?.base64,
          contentType: result?.contentType,
        });
        objectUrlRef.current = objectUrl;
        navigateLetterTemplatePdfPreviewWindow(previewWindow, objectUrl);
      } catch (err) {
        closeLetterTemplatePdfPreviewWindow(previewWindow);
        setErrorKey(adminProspectLetterTemplatePdfPreviewErrorKeyFromError(err));
        setRetryable(classifyAdminCallableError(err) === 'unavailable');
      } finally {
        inFlightRef.current = false;
        setPending(false);
      }
    },
    [enabled, revokeCurrentUrl],
  );

  return {
    pending,
    errorKey,
    retryable,
    previewDraft,
    clearError: () => {
      setErrorKey(null);
      setRetryable(false);
    },
  };
}
